import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { toast } from "react-toastify";
import { FiUsers, FiCheck, FiTrash2, FiRefreshCw, FiClock, FiCheckCircle, FiXCircle } from "react-icons/fi";
import {
    Button,
    Card,
    PageHeader,
    StatCard,
    StatusBadge,
    DataTable,
    EmptyState,
    Tooltip,
    TableSkeleton,
    useConfirm,
} from "./ui";

export default function ReferalDaftar() {
  const confirm = useConfirm();

  const [referalList, setReferalList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReferal();

    const referalSubscription = supabase
      .channel("public:referal")
      .on("postgres_changes", { event: "*", schema: "public", table: "referal" }, () =>
        fetchReferal()
      )
      .subscribe();

    return () => supabase.removeChannel(referalSubscription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReferal = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("referal")
      .select("id, user_id, nama_lengkap, kode_toko, dana_number, margin_max, kode_referal, status, saldo_aktif, saldo_tarik")
      .order("created_at", { ascending: false });

    if (error) {
      setError(error.message);
      setReferalList([]);
    } else {
      setReferalList(data);
      setError(null);
    }
    setLoading(false);
  };

  const handleApproveReferal = async (telegramUserId) => {
    const ok = await confirm({
      title: "Setujui Referal",
      message: "Setujui referal ini dan kirim notifikasi ke bot?",
      confirmLabel: "Setujui",
    });
    if (!ok) return;

    const { error } = await supabase
      .from("referal")
      .update({ status: "disetujui" })
      .eq("user_id", telegramUserId);

    if (error) {
      toast.error("Gagal approve referal: " + error.message);
      return;
    }

    fetch("/api/approve_referal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: telegramUserId })
    })
      .then(res => res.json())
      .then(() => {
        toast.success("Referal disetujui dan pesan dikirim!");
      })
      .catch(() => {
        toast.warn("Referal disetujui, tapi gagal kirim ke bot.");
      });
  };

  const handleRejectReferal = async (id) => {
    const ok = await confirm({
      title: "Tolak Referal",
      message: "Yakin ingin menolak referal ini?",
      confirmLabel: "Tolak",
      tone: "danger",
    });
    if (!ok) return;

    const { error } = await supabase
      .from("referal")
      .update({ status: "rejected" })
      .eq("id", id);

    if (error) {
      toast.error("Gagal menolak referal: " + error.message);
      return;
    }

    toast.success("Referal ditolak");
    fetchReferal();
  };

  const handleDeleteReferal = async (id, userId) => {
    const userIdStr = String(userId);

    const { data: penarikanData, error: checkError } = await supabase
      .from("penarikan")
      .select("id")
      .eq("user_id", userIdStr);

    if (checkError) {
      toast.error("Gagal memeriksa data penarikan: " + checkError.message);
      return;
    }

    const hasPenarikan = penarikanData && penarikanData.length > 0;
    const ok = await confirm({
      title: "Hapus Referal",
      message: hasPenarikan
        ? `Referal ini memiliki ${penarikanData.length} data penarikan terkait. Semua data penarikan akan ikut terhapus. Lanjutkan?`
        : "Yakin ingin menghapus referal ini?",
      confirmLabel: "Ya, Hapus",
      tone: "danger",
    });
    if (!ok) return;

    if (hasPenarikan) {
      const { error: deletePernarikanError } = await supabase
        .from("penarikan")
        .delete()
        .eq("user_id", userIdStr);

      if (deletePernarikanError) {
        toast.error("Gagal menghapus data penarikan: " + deletePernarikanError.message);
        return;
      }
    }

    const { error } = await supabase
      .from("referal")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Gagal menghapus referal: " + error.message);
      return;
    }

    toast.success("Referal berhasil dihapus");
    fetchReferal();
  };

  const counts = {
    total: referalList.length,
    pending: referalList.filter((r) => r.status === "pending" || r.status === "menunggu").length,
    approved: referalList.filter((r) => r.status === "disetujui").length,
    rejected: referalList.filter((r) => r.status === "rejected").length,
  };

  const columns = [
    {
      key: "nama_lengkap",
      header: "Nama",
      cell: (r) => <span className="font-semibold text-slate-900 dark:text-slate-100">{r.nama_lengkap}</span>,
    },
    {
      key: "kode_toko",
      header: "Kode Toko",
      hideBelow: "lg",
      cell: (r) => <span className="text-slate-600 dark:text-slate-400">{r.kode_toko}</span>,
    },
    {
      key: "dana_number",
      header: "DANA",
      hideBelow: "xl",
      cell: (r) => <span className="text-slate-600 dark:text-slate-400">{r.dana_number || "-"}</span>,
    },
    {
      key: "kode_referal",
      header: "Kode Referal",
      hideBelow: "lg",
      cell: (r) => <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{r.kode_referal || "-"}</span>,
    },
    {
      key: "margin_max",
      header: "Margin",
      hideBelow: "xl",
      cell: (r) => (
        <span className="text-slate-600 dark:text-slate-400">
          {r.margin_max !== null && r.margin_max !== undefined ? `Rp${r.margin_max.toLocaleString("id-ID")}` : "-"}
        </span>
      ),
    },
    {
      key: "saldo",
      header: "Saldo",
      cell: (r) => (
        <div className="text-xs">
          <p className="text-slate-700 dark:text-slate-300">Aktif: Rp{(r.saldo_aktif || 0).toLocaleString("id-ID")}</p>
          <p className="text-slate-500 dark:text-slate-400">Tarik: Rp{(r.saldo_tarik || 0).toLocaleString("id-ID")}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge status={r.status} size="sm" />,
    },
    {
      key: "aksi",
      header: "Aksi",
      headerClassName: "text-right",
      className: "text-right",
      cell: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          {r.status === "pending" ? (
            <>
              <Button variant="success" size="sm" icon={FiCheck} onClick={() => handleApproveReferal(r.user_id)}>
                Approve
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleRejectReferal(r.id)}>
                Reject
              </Button>
            </>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
          )}
          <Tooltip label="Hapus">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteReferal(r.id, r.user_id)}
              className="text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10"
              aria-label="Hapus referal"
            >
              <FiTrash2 className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Manajemen Referal"
        subtitle="Kelola pendaftaran dan komisi referal"
        icon={FiUsers}
        actions={
          <Tooltip label="Muat ulang">
            <Button variant="secondary" size="icon" onClick={fetchReferal} aria-label="Muat ulang">
              <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </Tooltip>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Total Referral" value={counts.total} icon={FiUsers} tone="primary" />
        <StatCard label="Pending" value={counts.pending} icon={FiClock} tone="warning" />
        <StatCard label="Approved" value={counts.approved} icon={FiCheckCircle} tone="success" />
        <StatCard label="Rejected" value={counts.rejected} icon={FiXCircle} tone="danger" />
      </div>

      <Card>
        {loading ? (
          <TableSkeleton rows={8} columns={5} />
        ) : error ? (
          <div className="m-4 rounded-xl border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300">
            {error}
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={referalList}
            rowKey={(r) => r.id}
            empty={<EmptyState icon={FiUsers} title="Belum ada referal" description="Pendaftaran referal akan tampil di sini." />}
          />
        )}
      </Card>
    </div>
  );
}
